// ***************************************************************************************
// The game state machine. Pure, deterministic, and runs unchanged in the browser and in
// the Worker - which is the whole point, because the Worker re-scores submissions with
// exactly this code rather than a second implementation that can drift.
//
// State is derived from the MOVE LIST, not accumulated in place. Undo is therefore
// "replay one fewer move" rather than "unwind the last one", which means:
//
//   - undo cannot restore a board that play could not have reached
//   - a save file is just the move list, so a restored game cannot be half-animated
//   - the Worker's verification and the client's play are literally the same function
//
// On a 5x5 a game is at most a handful of moves, so replaying from the start costs
// nothing measurable.
// ***************************************************************************************

import { settle, type Board } from './settle.ts';
import { MAX_TILES_PER_MOVE, WORD_LENGTH_MIN, scoreWord } from './rules.ts';
import type { Dictionary } from './dictionary.ts';
import type { Move, ModeDefinition, TileRef } from './modes.ts';
import { achievementMultiplier, claimedBy, type Achievement } from './achievements.ts';
import { shuffleBoard } from './shuffle.ts';

/** Bumped when a rule or a scoring number changes. Stored on every puzzle and result. */
export const RULES_VERSION = 1;

/** Bumped when board construction changes in a way that alters the boards it produces. */
export const GENERATOR_VERSION = 1;

/**
 * A puzzle's immutable identity.
 *
 * Persisted server-side at publication, so a later code or dictionary change cannot alter
 * a puzzle somebody is part-way through or has already scored on.
 */
export interface PuzzleIdentity {
  readonly puzzleId: string;
  readonly displayNumber: number;
  /** UTC date, YYYY-MM-DD. */
  readonly date: string;
  readonly day: number;
  readonly modeId: string;
  readonly boardMode: string;
  readonly w: number;
  readonly h: number;
  /** The starting tiles, [y * w + x], y = 0 at the bottom. */
  readonly tiles: readonly string[];
  readonly rulesVersion: number;
  readonly dictionaryVersion: string;
  readonly generatorVersion: number;
  readonly seed: number;
  /**
   * The generator's own words, when the site chooses to reveal them.
   *
   * ABSENT BY DEFAULT and absent on the main site. Ladder serves them behind the
   * REVEAL_SOLUTION flag as an experiment - see LadderPanel. Nothing in the engine reads
   * this; it is display only, and no rule depends on it.
   */
  readonly solution?: readonly string[];
  /**
   * Hunt mode's five challenges for the day.
   *
   * Published WITH the puzzle rather than derived on each side, because claiming one
   * changes the score and the Worker has to agree about which five were on offer.
   */
  readonly achievements?: readonly Achievement[];
  /** How many shuffles this puzzle allows. 0, and absent, mean none. */
  readonly shufflesAllowed?: number;
}

/**
 * One entry in the move log.
 *
 * A game is no longer only a list of words: Hunt lets a player SHUFFLE, and a shuffle
 * changes the board, so it has to be recorded in sequence with the words or a replay
 * lands on a different board.
 */
export type GameAction =
  | { readonly kind: 'word'; readonly tiles: readonly TileRef[] }
  | { readonly kind: 'shuffle' };

export type FinishReason = 'cleared' | 'no-moves' | 'resigned';

export interface GameState {
  readonly puzzle: PuzzleIdentity;
  readonly modeId: string;
  readonly board: Board;
  /**
   * THE AUTHORITATIVE LOG: everything that happened, in order, words and shuffles alike.
   * `moves` below is the words picked out of it for convenience.
   */
  readonly actions: readonly GameAction[];
  readonly moves: readonly Move[];
  readonly score: number;
  readonly shufflesUsed: number;
  /** Achievement ids taken so far, in the order they were claimed. */
  readonly claimed: readonly string[];
  readonly status: 'playing' | 'finished';
  readonly finishReason: FinishReason | null;
}

export interface SelectionPricing {
  readonly word: string;
  readonly score: number;
  readonly tier: number;
  readonly valid: boolean;
  /** Why it would be refused, for the UI to show. Null when valid. */
  readonly reason: string | null;
}

export interface MoveResult {
  readonly ok: true;
  readonly state: GameState;
  readonly move: Move;
}

export interface MoveFailure {
  readonly ok: false;
  readonly reason: string;
}

export function startingBoard(puzzle: PuzzleIdentity): Board {
  return puzzle.tiles.map((c) => (c === '' ? null : c));
}

export function createGame(puzzle: PuzzleIdentity, mode: ModeDefinition): GameState {
  return {
    puzzle,
    modeId: mode.id,
    board: startingBoard(puzzle),
    actions: [],
    moves: [],
    score: 0,
    shufflesUsed: 0,
    claimed: [],
    status: 'playing',
    finishReason: null,
  };
}

/** How many shuffles are left. Zero for any mode that does not offer them. */
export function shufflesLeft(state: GameState): number {
  return Math.max(0, (state.puzzle.shufflesAllowed ?? 0) - state.shufflesUsed);
}

// ***************************************************************************************
// Selection checking.
//
// Split from applyMove so the UI can price a selection live without committing it, using
// the same code that will accept or refuse it. A selection that looks valid on screen and
// is then refused on submit is the kind of bug this arrangement makes impossible.
// ***************************************************************************************
export function readSelection(
  board: Board,
  w: number,
  h: number,
  tiles: readonly TileRef[],
  /**
   * Whether each tile must touch the one before it.
   *
   * True everywhere but Freedom, where the whole point is that it does not. Defaulted so
   * that a caller which forgets gets the STRICTER rule: a mode that quietly stopped
   * checking adjacency would accept words no other mode would, and the Worker would
   * verify them happily because it runs this same function.
   */
  adjacency: boolean = true,
): { word: string; reason: string | null } {
  if (tiles.length === 0) return { word: '', reason: 'nothing selected' };

  if (tiles.length > MAX_TILES_PER_MOVE) {
    return { word: '', reason: 'selection is too long' };
  }

  const used = new Set<number>();
  let word = '';

  for (let i = 0; i < tiles.length; i++) {
    const tile = tiles[i]!;

    if (tile.x < 0 || tile.y < 0 || tile.x >= w || tile.y >= h) {
      return { word, reason: 'tile is off the board' };
    }

    const at = tile.y * w + tile.x;
    const letter = board[at];

    if (letter == null) return { word, reason: 'tile is not there' };

    if (used.has(at)) return { word, reason: 'the same tile twice' };

    used.add(at);

    if (adjacency && i > 0) {
      const previous = tiles[i - 1]!;
      const dx = Math.abs(tile.x - previous.x);
      const dy = Math.abs(tile.y - previous.y);

      // 8-way. Diagonals ARE allowed - see RULES.md section 2.
      if (dx > 1 || dy > 1) return { word, reason: 'tiles are not adjacent' };
    }

    word += letter;
  }

  return { word, reason: null };
}

export function priceSelection(
  state: GameState,
  tiles: readonly TileRef[],
  dictionary: Dictionary,
  mode: ModeDefinition,
): SelectionPricing {
  const { w, h } = state.puzzle;
  const { word, reason } = readSelection(state.board, w, h, tiles, mode.requiresAdjacency);

  if (reason) return { word, score: 0, tier: 0, valid: false, reason };

  if (word.length < Math.max(WORD_LENGTH_MIN, mode.minWordLength)) {
    return {
      word,
      score: 0,
      tier: 0,
      valid: false,
      reason: `${Math.max(WORD_LENGTH_MIN, mode.minWordLength)} letters minimum`,
    };
  }

  if (!dictionary.has(word)) {
    return { word, score: 0, tier: 0, valid: false, reason: 'not a word' };
  }

  const verdict = mode.validateMove(word, state.moves);

  if (!verdict.ok) return { word, score: 0, tier: 0, valid: false, reason: verdict.reason };

  const tier = dictionary.tier(word);

  return { word, score: scoreWord(word, tier), tier, valid: true, reason: null };
}

// ***************************************************************************************
// Committing a move.
//
// The logical state is updated ONCE, here. Animation is told what happened afterwards and
// cannot change it - if a delayed callback were allowed to apply game logic, a fast second
// submission during the animation would apply it twice.
// ***************************************************************************************
export function applyMove(
  state: GameState,
  tiles: readonly TileRef[],
  dictionary: Dictionary,
  mode: ModeDefinition,
): MoveResult | MoveFailure {
  if (state.status !== 'playing') return { ok: false, reason: 'the game is over' };

  const pricing = priceSelection(state, tiles, dictionary, mode);

  if (!pricing.valid) return { ok: false, reason: pricing.reason ?? 'not a word' };

  const { w, h } = state.puzzle;
  const board = state.board.slice();

  for (const tile of tiles) board[tile.y * w + tile.x] = null;

  if (!settle(board, w, h)) return { ok: false, reason: 'the board could not settle' };

  // The move at its BASE value first, because achievements are evaluated against the
  // word as played - `big-score` compares to this, not to a total that includes its own
  // bonus.
  const base: Move = {
    tiles: tiles.map((t) => ({ x: t.x, y: t.y })),
    word: pricing.word,
    score: pricing.score,
    tier: pricing.tier,
    claimed: [],
  };

  const achievements = state.puzzle.achievements ?? [];
  const taken = achievements.length
    ? claimedBy(achievements, new Set(state.claimed), base)
    : [];

  const move: Move = {
    ...base,
    score: pricing.score * achievementMultiplier(taken.length),
    claimed: taken,
  };

  const moves = [...state.moves, move];
  const actions: GameAction[] = [...state.actions, { kind: 'word', tiles: move.tiles }];
  const score = state.score + move.score;
  const claimed = taken.length ? [...state.claimed, ...taken] : state.claimed;

  return {
    ok: true,
    move,
    state: finishIfDone(
      { ...state, board, actions, moves, score, claimed },
      dictionary,
      mode,
    ),
  };
}

// ***************************************************************************************
// Shuffle.
//
// Rearranges the letters among the tiles already on the board. Recorded in the action log
// like a word, because it changes the board and a replay has to reproduce it.
// ***************************************************************************************
export function applyShuffle(state: GameState): MoveResult | MoveFailure {
  if (state.status !== 'playing') return { ok: false, reason: 'the game is over' };

  if (shufflesLeft(state) <= 0) return { ok: false, reason: 'no shuffles left' };

  const board = shuffleBoard(state.board, state.puzzle.seed, state.shufflesUsed);

  return {
    ok: true,
    move: { tiles: [], word: '', score: 0, tier: 0, claimed: [] },
    state: {
      ...state,
      board,
      actions: [...state.actions, { kind: 'shuffle' }],
      shufflesUsed: state.shufflesUsed + 1,
    },
  };
}

/**
 * Undo the last move by replaying one fewer. Restores the board, the score and the word
 * history together, because all three are derived from the same list.
 */
export function undo(
  state: GameState,
  dictionary: Dictionary,
  mode: ModeDefinition,
): GameState {
  if (state.actions.length === 0) return state;

  // One fewer ACTION, so undoing a shuffle undoes the shuffle rather than the word before
  // it. Replaying from the start is what keeps a shuffled board reproducible.
  return replayActions(state.puzzle, state.actions.slice(0, -1), dictionary, mode).state;
}

/** A fresh attempt. Never touches a recorded best result - that lives outside the game. */
export function restart(state: GameState, mode: ModeDefinition): GameState {
  return createGame(state.puzzle, mode);
}

/** Finish early. The player asked; the UI is responsible for confirming first. */
export function resign(state: GameState): GameState {
  if (state.status !== 'playing') return state;

  return { ...state, status: 'finished', finishReason: 'resigned' };
}

export function tilesRemaining(board: Board): number {
  let count = 0;

  for (const cell of board) {
    if (cell != null) count++;
  }

  return count;
}

export function isCleared(board: Board): boolean {
  return board.every((cell) => cell == null);
}

// ***************************************************************************************
// Completion.
//
// Two ways a game ends on its own: the board is empty, or no legal word remains. The
// second is expensive to establish, so the caller may pass a precomputed answer from the
// search worker. A TIMEOUT IS NOT AN ANSWER - see hasAnyMove in solver.ts. When the
// search does not know, the game stays playable.
// ***************************************************************************************
export function finishIfDone(
  state: GameState,
  _dictionary: Dictionary,
  _mode: ModeDefinition,
  noMovesRemain?: boolean,
): GameState {
  if (state.status !== 'playing') return state;

  if (isCleared(state.board)) {
    return { ...state, status: 'finished', finishReason: 'cleared' };
  }

  if (noMovesRemain === true) {
    return { ...state, status: 'finished', finishReason: 'no-moves' };
  }

  return state;
}

// ***************************************************************************************
// Replay a whole game from its move list.
//
// This is what the Worker runs against a submission, and what undo and save-restore run
// on the client. One implementation, three callers.
// ***************************************************************************************
export interface ReplayResult {
  readonly state: GameState;
  readonly ok: boolean;
  /** The index of the first move that could not have been played, or -1. */
  readonly failedAt: number;
  readonly reason: string | null;
}

export function replayActions(
  puzzle: PuzzleIdentity,
  actions: readonly GameAction[],
  dictionary: Dictionary,
  mode: ModeDefinition,
): ReplayResult {
  let state = createGame(puzzle, mode);

  for (let i = 0; i < actions.length; i++) {
    const action = actions[i]!;

    const result =
      action.kind === 'shuffle'
        ? applyShuffle(state)
        : applyMove(state, action.tiles, dictionary, mode);

    if (!result.ok) {
      return { state, ok: false, failedAt: i, reason: result.reason };
    }

    state = result.state;
  }

  return { state, ok: true, failedAt: -1, reason: null };
}

/** Replay a word-only log. Kept because most callers have no shuffles to express. */
export function replayMoves(
  puzzle: PuzzleIdentity,
  moves: readonly { tiles: readonly TileRef[] }[],
  dictionary: Dictionary,
  mode: ModeDefinition,
): ReplayResult {
  return replayActions(
    puzzle,
    moves.map((move) => ({ kind: 'word' as const, tiles: move.tiles })),
    dictionary,
    mode,
  );
}
